/**
 * The cash ledger's pure rules.
 *
 * Sources: `DATABASE-PROPOSAL.md` §4 ("Actual-ledger eligibility"), `TECHNICAL-SPECIFICATION.md`
 * §4 (the balance/opening/activity algebra, and the reporting sums that special entries never
 * enter) and `IMPLEMENTATION-CONTRACTS.md` §3 (the per-type sign rule and the name key).
 *
 * Nothing here reads a clock, a database or the network. The eligibility and summation rules
 * live here — once, exactly — so the API's SQL and this module cannot drift apart in
 * interpretation: the SQL selects rows, this decides what their signed amount does.
 */
import { InvalidMoneyError, isWithinIndividualBound } from './money.js';
export class InvalidLedgerError extends Error {
    name = 'InvalidLedgerError';
}
/** Ordinary = the rows a report sums. Openings and transfers are specials. */
export function isOrdinary(type) {
    return type === 'income' || type === 'expense';
}
/**
 * The one sign rule per type (DATABASE-PROPOSAL.md §3): ordinary income is positive, ordinary
 * expense negative, an opening signed or zero. A transfer leg's sign is its side of the pair,
 * which the transfer service owns; this rejects only what it can see.
 */
export function validateEntrySign(type, signedMinor) {
    if (!isWithinIndividualBound(signedMinor)) {
        throw new InvalidMoneyError(`outside the individual money bound: ${signedMinor}`);
    }
    if (type === 'income' && signedMinor <= 0n) {
        throw new InvalidLedgerError('ordinary income must be a positive amount');
    }
    if (type === 'expense' && signedMinor >= 0n) {
        throw new InvalidLedgerError('ordinary expense must be a negative amount');
    }
}
/**
 * The derived business date (DATABASE-PROPOSAL.md §4).
 *
 * An opening uses its own opening DATE; every other type uses its own due DATE. A transfer leg
 * carries no date of its own — the parent owns the shared transfer DATE — so it uses that.
 * `null` means the row has no date this ledger can place, which is a shape the database's
 * `entry_date_shape` constraint does not allow, so it can only come from a caller that built the
 * object by hand, and it is never silently treated as "today".
 */
export function effectiveDate(entry) {
    if (entry.type === 'opening')
        return entry.openingDate;
    if (entry.type === 'transfer')
        return entry.transferDate ?? null;
    return entry.dueDate;
}
/**
 * Whether the row actually moves money (DATABASE-PROPOSAL.md §4).
 *
 * - cash ordinary: only when `paid = true`;
 * - card ordinary: always date-effective, whatever the payment transfers say;
 * - opening: active, therefore eligible;
 * - transfer leg: only when its parent is completed.
 *
 * A canceled row is never eligible.
 */
export function isActualEligible(entry) {
    if (entry.canceled === true)
        return false;
    switch (entry.type) {
        case 'income':
        case 'expense':
            return entry.accountKind === 'card' ? true : entry.paid === true;
        case 'opening':
            return true;
        case 'transfer':
            return entry.transferCompleted === true;
    }
}
/**
 * `Balance(a,t) = Σ signed(e)` for actual-eligible `e` with `date(e) <= t`
 * (TECHNICAL-SPECIFICATION.md §4). `cutoff` is inclusive.
 */
export function balanceAt(entries, cutoff) {
    let total = 0n;
    for (const entry of entries) {
        if (!isActualEligible(entry))
            continue;
        const date = effectiveDate(entry);
        if (date !== null && date <= cutoff)
            total += entry.signedMinor;
    }
    return total;
}
/**
 * `Opening(a,start) = Σ signed(e)` for actual-eligible dates `< start`
 * (TECHNICAL-SPECIFICATION.md §4). Periods are half-open: `[start,end)`.
 */
export function openingSum(entries, start) {
    let total = 0n;
    for (const entry of entries) {
        if (!isActualEligible(entry))
            continue;
        const date = effectiveDate(entry);
        if (date !== null && date < start)
            total += entry.signedMinor;
    }
    return total;
}
/** `Activity(a,start,end) = Σ signed(e)` for actual-eligible dates in `[start,end)`. */
export function activitySum(entries, start, end) {
    let total = 0n;
    for (const entry of entries) {
        if (!isActualEligible(entry))
            continue;
        const date = effectiveDate(entry);
        if (date !== null && date >= start && date < end)
            total += entry.signedMinor;
    }
    return total;
}
/** `Closing = Opening + Activity`. */
export function closingBalance(entries, start, end) {
    return openingSum(entries, start) + activitySum(entries, start, end);
}
/**
 * Reporting sums over *ordinary* entries by due DATE in `[start,end)`
 * (TECHNICAL-SPECIFICATION.md §4, last paragraph).
 *
 * Income and expense are reported as positive magnitudes. **Special entries never enter these
 * sums**: an opening is not income, and a transfer is neither — which is why this reads
 * `isOrdinary` rather than the eligibility rule.
 */
export function ordinaryReport(entries, start, end) {
    let income = 0n;
    let expense = 0n;
    for (const entry of entries) {
        if (entry.canceled === true || !isOrdinary(entry.type))
            continue;
        const date = effectiveDate(entry);
        if (date === null || date < start || date >= end)
            continue;
        if (entry.type === 'income')
            income += entry.signedMinor;
        else
            expense += -entry.signedMinor;
    }
    return { incomeMinor: income, expenseMinor: expense, netMinor: income - expense };
}
/**
 * The owner-scoped name key (register §4: "Names use owner-scoped normalized keys: NFC, trim,
 * locale-independent lowercase").
 */
export function normalizeNameKey(name) {
    return name.normalize('NFC').trim().toLowerCase();
}
/**
 * Today, in the business time zone, as a strict business DATE (register §7).
 *
 * `America/Winnipeg` decides Today, overdue, card state and edit eligibility
 * (TECHNICAL-SPECIFICATION.md §3), and the conversion is done through `Intl` rather than by
 * shifting a UTC instant — a business DATE is never a JavaScript `Date`.
 */
export function todayInTimeZone(timeZone, instant) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(instant);
    const read = (type) => {
        const found = parts.find((part) => part.type === type);
        if (found === undefined)
            throw new InvalidLedgerError(`no ${type} in the formatted date`);
        return found.value;
    };
    return `${read('year')}-${read('month')}-${read('day')}`;
}
//# sourceMappingURL=ledger.js.map